import React, { useState, useEffect } from 'react'
import { env } from '../config/environment'
import { 
  Card, 
  Typography, 
  Upload, 
  Button, 
  message, 
  List, 
  Space, 
  Alert,
  Spin,
  Progress,
  Tag,
  Row,
  Col,
  Divider,
  Modal,
  Image
} from 'antd'
import { 
  UploadOutlined, 
  FileOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  ArrowLeftOutlined,
  InboxOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileUnknownOutlined
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import type { UploadProps, UploadFile } from 'antd/es/upload/interface'
import { customerApi } from '../services/customerApi'

const { Title, Text } = Typography
const { Dragger } = Upload

interface JobDocument {
  id: string
  filename: string
  originalName: string
  mimeType: string
  size: number
  uploadedAt: string
  url?: string
}

const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) {
    return (
      <div style={{ position: 'relative' }}>
        <FileImageOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
        <div style={{ 
          position: 'absolute', 
          top: '-2px', 
          right: '-2px', 
          background: '#52c41a', 
          borderRadius: '50%', 
          width: '6px', 
          height: '6px' 
        }} />
      </div>
    )
  }
  if (mimeType.includes('pdf')) return <FilePdfOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
  if (mimeType.includes('word') || mimeType.includes('document')) return <FileWordOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileExcelOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
  return <FileOutlined style={{ fontSize: '20px', color: '#666' }} />
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const DocumentUpload: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const [uploading, setUploading] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [documents, setDocuments] = useState<JobDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [jobTitle, setJobTitle] = useState<string>('')
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewDocument, setPreviewDocument] = useState<JobDocument | null>(null)

  useEffect(() => {
    if (jobId) {
      loadJobInfo()
      loadDocuments()
    }
  }, [jobId])

  const loadJobInfo = async () => {
    try {
      // Get basic job info for display
      const job = await customerApi.getJob(jobId!)
      setJobTitle(job.title || `Job #${job.jobNumber}`)
    } catch (error) {
      console.error('Failed to load job info:', error)
    }
  }

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const response = await customerApi.getJobDocuments(jobId!)
      setDocuments(response.documents || [])
      console.log('📄 Loaded documents:', response.documents?.length || 0)
    } catch (error) {
      console.error('Failed to load documents:', error)
      message.error('Failed to load existing documents')
      setDocuments([])
    } finally {
      setLoading(false)
    }
  }

  const uploadProps: UploadProps = {
    name: 'files',
    multiple: true,
    fileList,
    beforeUpload: (file) => {
      // Check file size (10MB limit)
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        message.error('File must be smaller than 10MB!')
        return false
      }

      // Check file type
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ]
      
      if (!allowedTypes.includes(file.type)) {
        message.error('File type not supported! Please upload JPG, PNG, PDF, DOC, DOCX, XLS, or XLSX files.')
        return false
      }

      return false // Prevent automatic upload
    },
    onChange: (info) => {
      setFileList(info.fileList)
    },
    onDrop: (e) => {
      console.log('Dropped files', e.dataTransfer.files)
    }
  }

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select files to upload')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj)
        }
      })

      await customerApi.uploadJobDocuments(jobId!, formData)
      
      message.success('Documents uploaded successfully!')
      setFileList([])
      loadDocuments() // Refresh document list
      
    } catch (error: any) {
      console.error('Upload failed:', error)
      message.error(error.response?.data?.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleRemoveFile = (file: UploadFile) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid)
    setFileList(newFileList)
  }

  const isImageFile = (mimeType: string) => {
    return mimeType.startsWith('image/')
  }

  const getDocumentUrl = (doc: JobDocument) => {
    // Files are now stored in Google Cloud Storage and have full URLs
    // If the URL starts with https://, it's already a full URL (GCS)
    // Otherwise, fall back to the old local serving method for backward compatibility
    if (doc.url?.startsWith('https://')) {
      return doc.url
    } else {
      // Fallback for old local files
      const baseUrl = env.apiBaseUrl.replace('/api', '')
      return `${baseUrl}${doc.url}`
    }
  }

  const handleViewDocument = (doc: JobDocument) => {
    if (isImageFile(doc.mimeType)) {
      // Show image in modal
      setPreviewDocument(doc)
      setPreviewVisible(true)
    } else {
      // Open document in new tab/window
      const fullUrl = getDocumentUrl(doc)
      window.open(fullUrl, '_blank')
    }
  }

  const handleDeleteDocument = async (docId: string) => {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this document? This action cannot be undone.')
      
      if (confirmed) {
        console.log('🗑️ Deleting document:', docId)
        await customerApi.deleteJobDocument(jobId!, docId)
        message.success('Document deleted successfully!')
        loadDocuments() // Reload the document list
      }
    } catch (error) {
      console.error('Failed to delete document:', error)
      message.error('Failed to delete document')
    }
  }

  if (!jobId) {
    return (
      <Alert
        message="Invalid Job"
        description="No job ID provided"
        type="error"
        showIcon
      />
    )
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(`/jobs/${jobId}`)}
          style={{ marginBottom: '16px' }}
        >
          Back to Job Details
        </Button>
        <Title level={2}>Upload Documents</Title>
        <Text type="secondary">
          {jobTitle ? `For: ${jobTitle}` : 'Loading job information...'}
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* Upload Section */}
        <Col xs={24} lg={14}>
          <Card title="Upload New Documents">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag files to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for single or bulk upload. Supported formats: JPG, PNG, PDF, DOC, DOCX, XLS, XLSX. Maximum file size: 10MB.
              </p>
            </Dragger>

            {fileList.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Divider>Selected Files</Divider>
                <List
                  size="small"
                  dataSource={fileList}
                  renderItem={(file) => (
                    <List.Item
                      actions={[
                        <Button 
                          size="small" 
                          danger 
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveFile(file)}
                        >
                          Remove
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={getFileIcon(file.type || '')}
                        title={file.name}
                        description={`${formatFileSize(file.size || 0)} • ${file.type}`}
                      />
                    </List.Item>
                  )}
                />
                
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <Button 
                    type="primary" 
                    onClick={handleUpload}
                    loading={uploading}
                    disabled={fileList.length === 0}
                    size="large"
                  >
                    {uploading ? 'Uploading...' : `Upload ${fileList.length} File${fileList.length > 1 ? 's' : ''}`}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* Existing Documents */}
        <Col xs={24} lg={10}>
          <Card title="Uploaded Documents">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
                <p>Loading documents...</p>
              </div>
            ) : documents.length > 0 ? (
              <List
                dataSource={documents}
                renderItem={(doc) => (
                  <List.Item style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '12px' }}>
                      {/* File Icon */}
                      <div style={{ flexShrink: 0 }}>
                        {getFileIcon(doc.mimeType)}
                      </div>
                      
                      {/* File Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: 500, 
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {doc.originalName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <Space split={<span style={{ color: '#d9d9d9' }}>•</span>}>
                            <span>{formatFileSize(doc.size)}</span>
                            <span>Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}</span>
                          </Space>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div style={{ flexShrink: 0 }}>
                        <Space>
                          <Button 
                            size="small" 
                            icon={<EyeOutlined />} 
                            type="text"
                            onClick={() => handleViewDocument(doc)}
                            title="View document"
                          />
                          <Button 
                            size="small" 
                            icon={<DeleteOutlined />} 
                            type="text" 
                            danger
                            onClick={() => handleDeleteDocument(doc.id)}
                            title="Delete document"
                          />
                        </Space>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <FileOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                <p style={{ marginTop: '16px', color: '#999' }}>
                  No documents uploaded yet
                </p>
              </div>
            )}
          </Card>

          {/* Upload Guidelines */}
          <Card title="Upload Guidelines" size="small" style={{ marginTop: '16px' }}>
            <Space direction="vertical" size="small">
              <Text strong>Accepted File Types:</Text>
              <div>
                <Tag color="green">JPG</Tag>
                <Tag color="green">PNG</Tag>
                <Tag color="red">PDF</Tag>
                <Tag color="blue">DOC</Tag>
                <Tag color="blue">DOCX</Tag>
              </div>
              
              <Text strong>File Size Limit:</Text>
              <Text>Maximum 10MB per file</Text>
              
              <Text strong>What to Upload:</Text>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Before/after photos</li>
                <li>Receipts and invoices</li>
                <li>Warranty documents</li>
                <li>Technical specifications</li>
                <li>Inspection reports</li>
              </ul>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Image Preview Modal */}
      <Modal
        open={previewVisible}
        title={previewDocument?.originalName}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        style={{ maxWidth: '800px' }}
      >
        {previewDocument && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={getDocumentUrl(previewDocument)}
              alt={previewDocument.originalName}
              style={{ maxWidth: '100%' }}
              preview={false}
            />
            <div style={{ marginTop: '16px', textAlign: 'left' }}>
              <Space direction="vertical" size="small">
                <Text strong>File Name:</Text>
                <Text>{previewDocument.originalName}</Text>
                <Text strong>File Size:</Text>
                <Text>{formatFileSize(previewDocument.size)}</Text>
                <Text strong>Upload Date:</Text>
                <Text>{new Date(previewDocument.uploadedAt).toLocaleDateString()}</Text>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
